import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  IDispositivoAlarma,
  IDispositivoLorawan,
  IEntidades,
  IFilter,
  IMaterialServicioTecnico,
  IQueryParam,
  ITracker,
  IUsuario,
} from 'modelos/src';
import { MaterialModule } from '../../../auxiliares/material.module';
import {
  HelperService,
  Modulo,
} from '../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../auxiliares/servicios/lista2.service';
import { DialogService } from '../../../auxiliares/dialog/dialog.service';
import { MaterialesServicioTecnicoService } from '../../../auxiliares/servicios/http/materiales-servicio-tecnico.service';
import { IUsuarioConPermiso } from '../../../auxiliares/servicios/http/usuarios.service';
import { LoginService } from '../../login/login.service';
import { CrearEditarComponent } from '../../../componentes/crear-editar/crear-editar.component';
import { ContentCardComponent } from '../../../componentes/content-card/content-card.component';
import { ButtonComponent } from '../../../auxiliares/button/button.component';
import { UploadFileComponent } from '../../../auxiliares/upload-file/upload-file.component';
import { InfoBannerComponent } from '../../../componentes/info-banner/info-banner.component';

export interface DataAsignarMaterialesTecnico {
  usuario: IUsuario; // Técnico (IUsuario) al que se le asignan materiales.
}

interface OpcionEntidad {
  id: string;
  label: string;
  // false cuando el dispositivo ya está asignado a OTRO técnico.
  disponible: boolean;
  // Nombre del técnico que lo tiene asignado (solo si no está disponible).
  asignadoA?: string;
  // ng-select deshabilita la opción cuando es true (= !disponible).
  disabled?: boolean;
}

interface TipoDispositivoAsignable {
  value: IEntidades;
  label: string;
  modulos: Modulo[]; // módulos que habilitan este tipo de dispositivo
}

@Component({
  selector: 'app-asignar-materiales-tecnico',
  templateUrl: './asignar-materiales-tecnico.component.html',
  styleUrl: './asignar-materiales-tecnico.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    NgSelectModule,
    CrearEditarComponent,
    ContentCardComponent,
    ButtonComponent,
    UploadFileComponent,
    InfoBannerComponent,
  ],
})
export class AsignarMaterialesTecnicoComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AsignarMaterialesTecnicoComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private data = inject<DataAsignarMaterialesTecnico>(MAT_DIALOG_DATA);
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private materialesService = inject(MaterialesServicioTecnicoService);
  private dialogService = inject(DialogService);

  public loading = false;
  public guardando = false;
  public usuario!: IUsuario;

  // Cliente del técnico resuelto por su Permiso (usuario.idCliente está deprecado
  // y vacío en técnicos auto-registrados). Fallback: cliente del operador logueado.
  private idClienteTecnico?: string;

  // Materiales ya asignados al técnico (tipo 'asignado')
  public asignados: IMaterialServicioTecnico[] = [];

  // Alta: material libre (cables, herramientas) o dispositivo del sistema
  public modo: 'libre' | 'dispositivo' = 'libre';

  // Material libre
  public descripcion = '';
  public cantidad = 1;

  // Fotos opcionales del material asignado (mismas que usado/pedido).
  public imagenes: string[] = [];

  // Dispositivos del sistema. Cada tipo se muestra solo si el usuario tiene el
  // módulo correspondiente. Extensible: agregar acá + un case en cargarDispositivos.
  private readonly TIPOS_DISPOSITIVO: TipoDispositivoAsignable[] = [
    {
      value: 'Dispositivo Lorawan',
      label: 'Dispositivo LoRaWAN',
      modulos: ['Luminarias'],
    },
    { value: 'Alarma', label: 'Alarma', modulos: ['Alarmas'] },
    {
      value: 'Tracker',
      label: 'Tracker',
      modulos: ['Vehículos', 'Activos', 'Colectivos'],
    },
  ];
  public tiposDispositivo: TipoDispositivoAsignable[] = [];
  public tipoEntidadSel?: IEntidades;
  public entidades: OpcionEntidad[] = [];
  public idEntidadSel?: string;
  public cargandoEntidades = false;

  async ngOnInit() {
    this.usuario = this.data.usuario;
    this.idClienteTecnico =
      (this.usuario as IUsuarioConPermiso).permiso?.idCliente ||
      LoginService.getCliente()?._id;
    // Tipos de dispositivo según los módulos del usuario.
    this.tiposDispositivo = this.TIPOS_DISPOSITIVO.filter((t) =>
      t.modulos.some((m) => this.helper.tieneModulo(m)),
    );
    await this.recargar();
  }

  private async recargar() {
    this.loading = true;
    try {
      await this.cargarAsignados();
      if (this.modo === 'dispositivo' && this.tipoEntidadSel) {
        await this.cargarEntidades();
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  private async cargarAsignados() {
    const filter: IFilter<IMaterialServicioTecnico> = {
      idUsuario: this.usuario._id,
      tipo: 'asignado',
      estado: 'Asignado',
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      unlimited: true,
      // El técnico puede pertenecer a un cliente hijo del operador logueado.
      // Sin includeChildren el filtro de permisos no incluye la rama
      // { idsAncestros } y el stock del hijo no se devuelve (igual que en
      // cargarEntidades, que ya lo setea).
      includeChildren: true,
    };
    const res = await this.materialesService.getFiltered(query);
    this.asignados = res.datos || [];
  }

  public async onTipoEntidadChange() {
    this.idEntidadSel = undefined;
    this.entidades = [];
    if (this.tipoEntidadSel) await this.cargarEntidades();
  }

  // Construye el mapa idEntidad -> técnico que lo tiene asignado (cualquier técnico),
  // para el tipo elegido. Permite marcar en el listado qué dispositivos no están
  // disponibles y quién los tiene.
  private async cargarAsignacionesEntidad(): Promise<
    Map<string, { idUsuario?: string; tecnico: string }>
  > {
    const filter: IFilter<IMaterialServicioTecnico> = {
      tipo: 'asignado',
      estado: 'Asignado',
      tipoEntidad: this.tipoEntidadSel,
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      unlimited: true,
      includeChildren: true,
      populate: JSON.stringify([
        { path: 'usuario', select: 'usuario datosPersonales' },
      ]),
    };
    const res = await this.materialesService.getFiltered(query);
    const mapa = new Map<string, { idUsuario?: string; tecnico: string }>();
    for (const m of res.datos || []) {
      if (!m.idEntidad) continue;
      const tecnico =
        m.usuario?.datosPersonales?.nombre ||
        m.usuario?.usuario ||
        'otro técnico';
      mapa.set(m.idEntidad, { idUsuario: m.idUsuario, tecnico });
    }
    return mapa;
  }

  // Carga los dispositivos del tipo elegido. Muestra TODOS, marcando cuáles no
  // están disponibles (asignados a otro técnico) y a quién. Los que ya tiene este
  // técnico se excluyen (aparecen en la lista de asignados). Extensible: agregar
  // un case por cada tipo soportado.
  private async cargarEntidades() {
    this.cargandoEntidades = true;
    try {
      const asignaciones = await this.cargarAsignacionesEntidad();
      // Arma la opción de un dispositivo resolviendo su disponibilidad.
      // Devuelve null si el dispositivo ya es de este técnico (se omite).
      const armar = (
        id: string | undefined,
        label: string,
      ): OpcionEntidad | null => {
        if (!id) return null;
        const asig = asignaciones.get(id);
        if (asig?.idUsuario === this.usuario._id) return null;
        return asig
          ? {
              id,
              label,
              disponible: false,
              asignadoA: asig.tecnico,
              disabled: true,
            }
          : { id, label, disponible: true };
      };
      // Dispositivos del cliente del técnico y de sus clientes descendientes.
      const idCli = this.idClienteTecnico;
      const query: IQueryParam = {
        filter: JSON.stringify({
          $or: [{ idCliente: idCli }, { idsAncestros: idCli }],
        }),
        unlimited: true,
        includeChildren: true,
      };

      switch (this.tipoEntidadSel) {
        case 'Dispositivo Lorawan': {
          const res = await firstValueFrom(
            this.lista2.dispositivosLorawans(query),
          );
          this.entidades = (res.datos || [])
            .filter((n: IDispositivoLorawan) => n.deveui)
            .map((n: IDispositivoLorawan) => armar(n._id, n.name || n.deveui!))
            .filter((o): o is OpcionEntidad => !!o);
          break;
        }
        case 'Alarma': {
          const res = await firstValueFrom(this.lista2.alarmas(query));
          this.entidades = (res.datos || [])
            .map((a: IDispositivoAlarma) =>
              armar(a._id, a.nombre || a.numeroAbonado || a._id!),
            )
            .filter((o): o is OpcionEntidad => !!o);
          break;
        }
        case 'Tracker': {
          const res = await firstValueFrom(this.lista2.trackers(query));
          this.entidades = (res.datos || [])
            .map((t: ITracker) =>
              armar(t._id, t.nombre || t.identificacion || t._id!),
            )
            .filter((o): o is OpcionEntidad => !!o);
          break;
        }
        default:
          this.entidades = [];
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.cargandoEntidades = false;
  }

  public get puedeAgregar(): boolean {
    if (this.guardando) return false;
    if (this.modo === 'libre') return this.descripcion.trim().length > 0;
    return !!this.tipoEntidadSel && !!this.idEntidadSel;
  }

  public async onModoChange() {
    if (this.modo === 'dispositivo' && this.tipoEntidadSel) {
      await this.cargarEntidades();
    }
  }

  public async agregar() {
    if (!this.puedeAgregar) return;
    this.guardando = true;
    try {
      const base = {
        idUsuario: this.usuario._id,
        idCliente: this.idClienteTecnico,
        tipo: 'asignado' as const,
        estado: 'Asignado' as const,
      };
      const imagenes = this.imagenes.length ? [...this.imagenes] : undefined;
      if (this.modo === 'libre') {
        await this.materialesService.create({
          ...base,
          descripcion: this.descripcion.trim(),
          cantidad: this.cantidad || 1,
          imagenes,
        });
      } else {
        const entidad = this.entidades.find((e) => e.id === this.idEntidadSel);
        if (entidad && !entidad.disponible) {
          this.helper.notifWarn(
            `El dispositivo ya está asignado a ${entidad.asignadoA}. Desasignalo primero.`,
          );
          this.guardando = false;
          return;
        }
        await this.materialesService.create({
          ...base,
          tipoEntidad: this.tipoEntidadSel,
          idEntidad: this.idEntidadSel,
          descripcion: entidad?.label,
          cantidad: 1,
          imagenes,
        });
      }
      this.helper.notifSuccess('Material asignado');
      this.resetForm();
      await this.recargar();
    } catch (error) {
      // 409 = entidad ya asignada a otro técnico (validación backend)
      console.error(error);
      this.helper.notifError(error);
    }
    this.guardando = false;
  }

  // Desasigna un material: lo ELIMINA por completo (no queda en el historial del
  // listado de materiales). Si era un dispositivo del sistema, queda libre para
  // otros técnicos. Marcar como usado/anular se hace desde el listado de materiales.
  public async quitar(material: IMaterialServicioTecnico) {
    if (!material._id) return;
    const nombre = material.descripcion || material.idEntidad || 'material';
    const ok = await this.dialogService.confirm(
      'Desasignar material',
      `¿Querés desasignar y eliminar "${nombre}"? No quedará en el historial.`,
    );
    if (!ok) return;
    this.guardando = true;
    try {
      await this.materialesService.delete(material._id);
      this.helper.notifSuccess('Material desasignado');
      await this.recargar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.guardando = false;
  }

  private resetForm() {
    this.descripcion = '';
    this.cantidad = 1;
    this.idEntidadSel = undefined;
    this.imagenes = [];
  }

  public cerrar() {
    this.dialogRef?.close();
  }
}
